import React, { useState, useEffect } from "react";
import { RefreshCw } from "lucide-react";
import {
  affiliateApi,
  buildAffiliateLink,
  buildWalkthroughLink,
  buildUniversityLink,
  copyToClipboard,
} from "../../utils/api";
import {
  formatCurrency,
  formatDate,
  formatPercentage,
  formatProductType,
  formatStatus,
} from "../../utils/formatting";
import AffiliateContentGenerator from "./AffiliateContentGenerator";
import "./styles/affiliate-dashboard.css";

function AffiliateDashboard({ onClose, user }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dashboardData, setDashboardData] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [generatorMode, setGeneratorMode] = useState(null);
  const [timeRange, setTimeRange] = useState("all");
  const [invites, setInvites] = useState(null); // Mako door invites (flag mako_invites; null = hidden)

  useEffect(() => {
    loadDashboardData();
  }, [timeRange]);

  useEffect(() => {
    let alive = true;
    affiliateApi
      .getInvites()
      .then((d) => { if (alive && d && d.ok) setInvites(d); })
      .catch(() => {}); // 404 when the flag is off — card simply doesn't render
    return () => { alive = false; };
  }, []);

  const inviteStatusLabel = (st) =>
    st === "converted" ? "paid member" : st === "joined" ? "signed up" : st === "revoked" ? "revoked" : "spot held";

  const loadDashboardData = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await affiliateApi.getDashboard(timeRange);
      setDashboardData(data);
    } catch (err) {
      setError(err.message || "Failed to load dashboard data");
      console.error("Dashboard error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async (link, key) => {
    if (!link) return;

    const success = await copyToClipboard(link);

    if (success) {
      setCopiedLink(key);
      setTimeout(() => setCopiedLink(null), 2000);
    }
  };

  const handleStripeConnect = async () => {
    try {
      const response = await affiliateApi.startStripeConnect();
      if (response.onboarding_url) {
        window.location.href = response.onboarding_url;
      }
    } catch (err) {
      alert("Failed to start Stripe Connect: " + err.message);
    }
  };

  if (loading) {
    return (
      <div className="affiliate-dashboard-overlay" onClick={onClose}>
        <div
          className="affiliate-dashboard hf-reveal-scale"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="dashboard-header">
            <div className="header-left">
              <img
                src="/Optimus Logos and Graphics/Optimus-Logo-Header.svg"
                alt="Optimus OS"
                className="header-logo"
              />
              <div className="header-title">
                <h1>💰 Affiliate Dashboard</h1>
              </div>
            </div>
            <button className="btn-icon close-btn" onClick={onClose}>
              ✕
            </button>
          </div>
          <div className="loading-state">
            <RefreshCw size={48} className="spin" />
            <p>Loading your affiliate data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="affiliate-dashboard-overlay" onClick={onClose}>
        <div
          className="affiliate-dashboard hf-reveal-scale"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="dashboard-header">
            <div className="header-left">
              <img
                src="/Optimus Logos and Graphics/Optimus-Logo-Header.svg"
                alt="Optimus OS"
                className="header-logo"
              />
              <div className="header-title">
                <h1>💰 Affiliate Dashboard</h1>
              </div>
            </div>
            <button className="btn-icon close-btn" onClick={onClose}>
              ✕
            </button>
          </div>
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h3>Failed to Load Dashboard</h3>
            <p>{error}</p>
            <button className="btn-primary" onClick={loadDashboardData}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Field names match the /api/affiliate/dashboard response
  // (a previous version destructured recentPayouts/nextPayout, which the
  // backend never sends — so payout history and the next-payout card
  // could never render)
  const {
    affiliate,
    stats,
    recentConversions,
    payoutHistory,
    upcomingPayout,
    nextPayoutDate,
    isNewAffiliate,
  } = dashboardData || {};

  // Show welcome state for new users who haven't enrolled
  if (isNewAffiliate) {
    return (
      <div className="affiliate-dashboard-overlay" onClick={onClose}>
        <div
          className="affiliate-dashboard hf-reveal-scale"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="dashboard-header">
            <div className="header-left">
              <img
                src="/Optimus Logos and Graphics/Optimus-Logo-Header.svg"
                alt="Optimus OS"
                className="header-logo"
              />
              <div className="header-title">
                <h1>💰 Affiliate Dashboard</h1>
              </div>
            </div>
            <button className="btn-icon close-btn" onClick={onClose}>
              ✕
            </button>
          </div>
          <div className="welcome-affiliate-state hf-reveal" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 40px',
            textAlign: 'center',
            minHeight: '400px'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>🚀</div>
            <h2 style={{ fontSize: '28px', marginBottom: '16px', color: 'var(--text-primary)' }}>
              Become an Optimus Affiliate
            </h2>
            <p style={{ fontSize: '16px', color: 'var(--text-secondary)', maxWidth: '500px', marginBottom: '32px', lineHeight: '1.6' }}>
              Earn <strong style={{ color: 'var(--accent-primary)' }}>30% recurring commission</strong> for every customer you refer to Optimus OS.
              Share your unique link, track conversions, and get paid monthly.
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '24px',
              marginBottom: '40px',
              width: '100%',
              maxWidth: '600px'
            }}>
              <div style={{ background: 'var(--bg-primary)', padding: '20px', borderRadius: '12px' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>💰</div>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>30% Commission</div>
              </div>
              <div style={{ background: 'var(--bg-primary)', padding: '20px', borderRadius: '12px' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔄</div>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Recurring Revenue</div>
              </div>
              <div style={{ background: 'var(--bg-primary)', padding: '20px', borderRadius: '12px' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>📊</div>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Real-time Tracking</div>
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  setLoading(true);
                  await affiliateApi.joinProgram();
                  await loadDashboardData();
                } catch (err) {
                  setLoading(false);
                  alert("Failed to join affiliate program: " + err.message);
                }
              }}
              style={{
                padding: '16px 48px',
                fontSize: '16px',
                fontWeight: '600',
                background: 'var(--hf-grad)',
                border: 'none',
                borderRadius: '12px',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 24px var(--shadow-accent)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              🎯 Join Affiliate Program
            </button>
          </div>
        </div>
      </div >
    );
  }

  return (
    <div className="affiliate-dashboard-overlay" onClick={onClose}>
      <div className="affiliate-dashboard hf-reveal-scale" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-left">
            <img
              src="/Optimus Logos and Graphics/Optimus-Logo-Header.svg"
              alt="Optimus OS"
              className="header-logo"
            />
            <div className="header-title">
              <h1>💰 Affiliate Dashboard</h1>
              <p className="header-subtitle">
                Welcome back, {user?.name || "Affiliate"}!
              </p>
            </div>
          </div>
          <div className="header-actions">
            <select
              className="time-range-selector"
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="all">All Time</option>
            </select>
            <div
              className={`status-badge ${affiliate?.status === "active" ? "active" : "pending"}`}
            >
              {/* dot comes from the global .status-badge::before — a local
                  span here doubled it (Brad catch 2026-09-03) */}
              {affiliate?.status === "active" ? "ACTIVE" : "PENDING"}
            </div>
            <button className="btn-icon close-btn" onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="dashboard-tabs">
          <div className="tabs-list">
            <button
              className={`tab-button ${activeTab === "overview" ? "active" : ""}`}
              onClick={() => setActiveTab("overview")}
            >
              Overview
            </button>
            <button
              className={`tab-button ${activeTab === "conversions" ? "active" : ""}`}
              onClick={() => setActiveTab("conversions")}
            >
              Conversions
            </button>
            <button
              className={`tab-button ${activeTab === "payouts" ? "active" : ""}`}
              onClick={() => setActiveTab("payouts")}
            >
              Payouts
            </button>
          </div>
          {affiliate?.affiliateCode && (
            <div className="affiliate-code-badge">
              Code:{" "}
              <span className="code-value">{affiliate.affiliateCode}</span>
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="dashboard-content">
          {activeTab === "overview" && (
            <>
              {/* Stats Cards */}
              <div className="stats-grid hf-stagger">
                <div className="stat-card hf-panel hf-lift">
                  <div className="stat-header">
                    <span className="stat-icon">👥</span>
                  </div>
                  <div className="stat-content">
                    <span className="stat-label">Total Clicks</span>
                    <span className="stat-value hf-count">
                      {stats?.totalClicks || 0}
                    </span>
                    {stats?.monthClicks !== undefined && (
                      <span className="stat-meta">
                        {stats.monthClicks} this month
                      </span>
                    )}
                  </div>
                </div>

                <div className="stat-card hf-panel hf-lift">
                  <div className="stat-header">
                    <span className="stat-icon">✨</span>
                  </div>
                  <div className="stat-content">
                    <span className="stat-label">Total Conversions</span>
                    <span className="stat-value hf-count">
                      {stats?.totalConversions || 0}
                    </span>
                    {stats?.monthConversions !== undefined && (
                      <span className="stat-meta">
                        {stats.monthConversions} this month
                      </span>
                    )}
                  </div>
                </div>

                <div className="stat-card hf-panel hf-lift">
                  <div className="stat-header">
                    <span className="stat-icon">📈</span>
                  </div>
                  <div className="stat-content">
                    <span className="stat-label">Conversion Rate</span>
                    <span className="stat-value accent-green hf-count">
                      {formatPercentage(stats?.conversionRate || 0)}
                    </span>
                    <span className="stat-meta">Click to signup ratio</span>
                  </div>
                </div>

                <div className="stat-card stat-card-highlight hf-panel hf-lift">
                  <div className="stat-header">
                    <span className="stat-icon">💰</span>
                  </div>
                  <div className="stat-content">
                    <span className="stat-label">Lifetime Earnings</span>
                    <span className="stat-value accent-yellow hf-count">
                      {formatCurrency(stats?.lifetimeEarnings || 0)}
                    </span>
                    {stats?.monthEarnings !== undefined && (
                      <span className="stat-meta">
                        {formatCurrency(stats.monthEarnings)} this month
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Affiliate Links Section */}
              <div className="affiliate-section">
                <div className="section-header">
                  <h3>Your Affiliate Links</h3>
                  <p className="section-subtitle">
                    Three ways to share. Every link carries your code — 30%
                    recurring commission on every signup, whichever door they
                    walk through.
                  </p>
                </div>

                {(affiliate?.affiliateCode
                  ? [
                      {
                        key: "walkthrough",
                        label: "🎬 The Free Walkthrough",
                        url: buildWalkthroughLink(affiliate.affiliateCode),
                        coaching:
                          "Best first touch. Send to anyone curious — founders doing $1M+ who feel behind on AI. It's free, it sells for you.",
                      },
                      {
                        key: "university",
                        label: "🎓 Optimus University (free resources)",
                        url: buildUniversityLink(affiliate.affiliateCode),
                        coaching:
                          "Lightest touch. For people who want to learn first — every path inside leads back to your link.",
                      },
                      {
                        key: "signup",
                        label: "🚀 Straight to Signup",
                        url: buildAffiliateLink(affiliate.affiliateCode),
                        coaching:
                          "For the ready ones. $1 first month, your code locked in.",
                      },
                    ]
                  : []
                ).map((row) => (
                  <div key={row.key} className="affiliate-link-row">
                    <div className="link-row-label">{row.label}</div>
                    <div className="affiliate-link-container">
                      <div className="link-display">{row.url}</div>
                      <button
                        className={`btn-primary hf-press hf-focus ${copiedLink === row.key ? "btn-success hf-glow-accent" : ""}`}
                        onClick={() => handleCopyLink(row.url, row.key)}
                      >
                        {copiedLink === row.key ? "✓ Copied!" : "📋 Copy"}
                      </button>
                    </div>
                    <p className="link-row-coaching">{row.coaching}</p>
                  </div>
                ))}
                {!affiliate?.affiliateCode && (
                  <div className="affiliate-link-container">
                    <div className="link-display">
                      Loading affiliate links...
                    </div>
                  </div>
                )}

                <div className="quick-actions">
                  <div
                    className="action-card action-card-clickable"
                    onClick={() => setGeneratorMode("social")}
                  >
                    <span className="action-icon">💡</span>
                    <span>Share on social media for maximum reach</span>
                  </div>
                  <div
                    className="action-card action-card-clickable"
                    onClick={() => setGeneratorMode("email")}
                  >
                    <span className="action-icon">✉️</span>
                    <span>Send personalized emails to your network</span>
                  </div>
                  <div
                    className="action-card action-card-clickable"
                    onClick={() => setGeneratorMode("ads")}
                  >
                    <span className="action-icon">🎯</span>
                    <span>Generate ad copy for paid campaigns</span>
                  </div>
                </div>

                {invites && (
                  <div className="affiliate-link-row" style={{ marginTop: "28px" }}>
                    <div className="link-row-label">
                      🦈 Invite your friends to Mako — {invites.remaining} of {invites.allowed} invites left
                    </div>
                    <div className="affiliate-link-container">
                      <div className="link-display">{invites.link}</div>
                      <button
                        className={`btn-primary hf-press hf-focus ${copiedLink === "mako-invite" ? "btn-success hf-glow-accent" : ""}`}
                        onClick={() => handleCopyLink(invites.link, "mako-invite")}
                        disabled={invites.remaining === 0}
                      >
                        {copiedLink === "mako-invite" ? "✓ Copied!" : "📋 Copy"}
                      </button>
                    </div>
                    <p className="link-row-coaching">
                      Mako by text is invite-only while the door opens in waves. A friend who signs up through
                      your link takes one of your spots, skips the list, and carries your code: if they upgrade
                      to Optimus OS, the commission is yours, same as any other door.
                    </p>
                    {invites.remaining === 0 && (
                      <p className="link-row-coaching">You're out of invites. Once your friends are in, ask us for more.</p>
                    )}
                    {invites.invites?.length > 0 && (
                      <ul style={{ listStyle: "none", padding: 0, margin: "10px 0 0", display: "grid", gap: "6px" }}>
                        {invites.invites.map((i) => (
                          <li key={i.id} style={{ display: "flex", justifyContent: "space-between", gap: "12px", fontSize: "13px", color: "var(--text-secondary)" }}>
                            <span>{i.name || i.email || i.phone || "friend"}{i.name && i.phone ? ` · ${i.phone}` : ""}</span>
                            <span style={{ fontWeight: 600, color: i.status === "converted" ? "var(--accent-green, #22c55e)" : "var(--text-primary)" }}>
                              {inviteStatusLabel(i.status)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>


              {/* Advanced Tracking Script Section */}
              <div className="affiliate-section" style={{ marginTop: '24px' }}>
                <div className="section-header">
                  <h3>🔧 Advanced: Custom Bridge Page Tracking</h3>
                  <p className="section-subtitle">
                    Building your own landing page? Add this script to your page's &lt;head&gt; tag to track conversions automatically.
                  </p>
                </div>

                <div className="code-block-container" style={{
                  background: 'var(--bg-primary)',
                  padding: '16px',
                  borderRadius: '8px',
                  position: 'relative'
                }}>
                  <pre style={{ margin: 0, overflowX: 'auto', color: 'var(--text-secondary)', fontSize: '13px' }}>
                    {`<script>
  window.OPTIMUS_CONFIG = {
    apiUrl: "https://osoptimus.com",
    defaultRef: "${affiliate.affiliateCode}"
  };
</script>
<script src="https://osoptimus.com/tracking.js"></script>`}
                  </pre>
                  <button
                    className="btn-secondary btn-sm"
                    style={{ position: 'absolute', top: '10px', right: '10px' }}
                    onClick={() => {
                      const script = `<script>
  window.OPTIMUS_CONFIG = {
    apiUrl: "https://osoptimus.com",
    defaultRef: "${affiliate.affiliateCode}"
  };
</script>
<script src="https://osoptimus.com/tracking.js"></script>`;
                      copyToClipboard(script);
                      alert("Script copied to clipboard!");
                    }}
                  >
                    Copy Snippet
                  </button>
                </div>
              </div>

              {/* Next Payout Card */}
              {upcomingPayout?.amount > 0 && (
                <div className="next-payout-card">
                  <div className="payout-card-header">
                    <h3>💸 Next Payout</h3>
                    <span className="badge badge-info">Scheduled</span>
                  </div>
                  <div className="payout-card-content">
                    <div className="payout-amount">
                      {formatCurrency(upcomingPayout.amount || 0)}
                    </div>
                    <div className="payout-date">
                      Expected: {formatDate(nextPayoutDate)}
                    </div>
                  </div>
                </div>
              )}

              {/* Stripe Connect CTA */}
              {!affiliate?.stripeConnectActive && (
                <div className="stripe-connect-cta">
                  <div className="cta-icon">🏦</div>
                  <div className="cta-content">
                    <h3>Setup Stripe Connect</h3>
                    <p>
                      Connect with Stripe to receive monthly payouts automatically.
                    </p>
                    <button
                      className="btn-primary"
                      onClick={handleStripeConnect}
                    >
                      Connect with Stripe →
                    </button>
                  </div>
                </div>
              )}

              {/* Recent Conversions */}
              {recentConversions && recentConversions.length > 0 && (
                <div className="recent-section">
                  <h3>Recent Conversions</h3>
                  <div className="conversions-list">
                    {recentConversions.slice(0, 5).map((conversion) => (
                      <div key={conversion.id} className="conversion-item">
                        <div className="conversion-info">
                          <div className="conversion-product">
                            {formatProductType(conversion.productType)}
                          </div>
                          <div className="conversion-date">
                            {formatDate(conversion.convertedAt)}
                          </div>
                        </div>
                        <div className="conversion-amounts">
                          <div className="conversion-sale">
                            Sale: {formatCurrency(conversion.saleAmount)}
                          </div>
                          <div className="conversion-commission">
                            {formatCurrency(conversion.commissionAmount)}
                          </div>
                        </div>
                        <span
                          className={`badge badge-${conversion.status === "paid" ? "success" : "warning"}`}
                        >
                          {formatStatus(conversion.status)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <button
                    className="btn-secondary"
                    onClick={() => setActiveTab("conversions")}
                  >
                    View All Conversions →
                  </button>
                </div>
              )}
            </>
          )}

          {activeTab === "conversions" && (
            <div className="tab-content">
              <div className="tab-header">
                <h3>All Conversions</h3>
                <p>Track all your successful referrals and commissions</p>
              </div>

              {!recentConversions || recentConversions.length === 0 ? (
                <div className="empty-state hf-reveal">
                  <div className="empty-icon">📊</div>
                  <h3>No Conversions Yet</h3>
                  <p>
                    Start sharing your affiliate link to earn commissions on
                    every signup.
                  </p>
                  <button
                    className="btn-primary"
                    onClick={() => setActiveTab("overview")}
                  >
                    View Affiliate Link
                  </button>
                </div>
              ) : (
                <div className="conversions-table">
                  <div className="table-header">
                    <div className="th">Date</div>
                    <div className="th">Product</div>
                    <div className="th">Sale</div>
                    <div className="th">Commission</div>
                    <div className="th">Status</div>
                  </div>
                  <div className="table-body">
                    {recentConversions.map((conversion) => (
                      <div key={conversion.id} className="table-row">
                        <div className="td">
                          {formatDate(conversion.convertedAt)}
                        </div>
                        <div className="td">
                          {formatProductType(conversion.productType)}
                        </div>
                        <div className="td">
                          {formatCurrency(conversion.saleAmount)}
                        </div>
                        <div className="td td-highlight">
                          {formatCurrency(conversion.commissionAmount)}
                        </div>
                        <div className="td">
                          <span
                            className={`badge badge-${conversion.status === "paid" ? "success" : "warning"}`}
                          >
                            {formatStatus(conversion.status)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "payouts" && (
            <div className="tab-content">
              <div className="tab-header">
                <h3>Payout History</h3>
                <p>View all your past and upcoming payouts</p>
              </div>

              {!payoutHistory || payoutHistory.length === 0 ? (
                <div className="empty-state hf-reveal">
                  <div className="empty-icon">💸</div>
                  <h3>No Payouts Yet</h3>
                  <p>
                    Payouts are processed monthly once you've earned commissions
                    from conversions.
                  </p>
                  {!affiliate?.stripeConnectActive && (
                    <button
                      className="btn-primary"
                      onClick={handleStripeConnect}
                    >
                      Connect to Stripe
                    </button>
                  )}
                </div>
              ) : (
                <div className="payouts-table">
                  <div className="table-header">
                    <div className="th">Date</div>
                    <div className="th">Period</div>
                    <div className="th">Commissions</div>
                    <div className="th">Amount</div>
                    <div className="th">Status</div>
                  </div>
                  <div className="table-body">
                    {payoutHistory.map((payout) => (
                      <div key={payout.id} className="table-row">
                        <div className="td">
                          {formatDate(payout.completedAt || payout.periodEnd)}
                        </div>
                        <div className="td">
                          {formatDate(payout.periodStart)} –{" "}
                          {formatDate(payout.periodEnd)}
                        </div>
                        <div className="td">
                          {payout.commissionCount} commissions
                        </div>
                        <div className="td td-highlight">
                          {formatCurrency(payout.amount)}
                        </div>
                        <div className="td">
                          <span
                            className={`badge badge-${payout.status === "completed" ? "success" : "warning"}`}
                          >
                            {formatStatus(payout.status)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Info */}
        <div className="dashboard-footer">
          <div className="footer-info">
            <div className="info-item">
              <span className="info-label">Commission Rate:</span>
              <span className="info-value">30% Lifetime Recurring</span>
            </div>
            <div className="info-item">
              <span className="info-label">Payout Schedule:</span>
              <span className="info-value">Monthly (1st of month)</span>
            </div>
            <div className="info-item">
              <span className="info-label">Protection Period:</span>
              <span className="info-value">60 days</span>
            </div>
          </div>
        </div>

        {/* Content Generator Modal */}
        <AffiliateContentGenerator
          isOpen={!!generatorMode}
          onClose={() => setGeneratorMode(null)}
          mode={generatorMode}
          affiliateCode={dashboardData?.affiliate?.affiliateCode}
          userName={dashboardData?.affiliate?.firstName || user?.name}
        />
      </div>
    </div >
  );
}

export default AffiliateDashboard;
